import { highlightColor, rememberHighlightColor, selectColorButtons, validHighlightColor } from "./highlight_colors.js"

export function filteredHighlights(records, color) {
  return color ? records.filter((highlight) => highlight.color === color) : records
}

export async function copyTextToClipboard(text, { clipboard = globalThis.navigator?.clipboard, document = globalThis.document } = {}) {
  if (!text) return false

  try {
    if (clipboard?.writeText) {
      await clipboard.writeText(text)
      return true
    }

    const input = document?.createElement?.("textarea")
    if (!input || !document.body) return false

    input.value = text
    input.readOnly = true
    input.className = "reader-debug__copy-source"
    document.body.append(input)
    input.select()
    const copied = document.execCommand?.("copy")
    input.remove()
    return copied === true
  } catch (_error) {
    return false
  }
}

export function renderHighlightList({ target, records, filterColor, palette, labels, onNavigate, onEdit, onShare, onRemove }) {
  if (records.length === 0) return renderEmptyState(target, labels.noHighlights)

  const visibleRecords = filteredHighlights(records, filterColor)
  if (visibleRecords.length === 0) return renderEmptyState(target, labels.noFilteredHighlights)

  target.replaceChildren(...visibleRecords.map((highlight) => {
    const color = highlightColor(palette, highlight.color)
    const item = target.ownerDocument.createElement("article")
    item.className = "annotation-card annotation-card--highlight"
    item.dataset.highlightColor = highlight.color
    item.style.setProperty("--highlight-color", color.hex)

    const quote = button(target, highlight.selected_text, "annotation-quote", () => onNavigate(highlight))
    const indicator = target.ownerDocument.createElement("span")
    indicator.className = "annotation-color"
    indicator.setAttribute("role", "img")
    indicator.setAttribute("aria-label", color.label)
    indicator.title = color.label
    item.append(quote, indicator)

    if (highlight.note) {
      const note = target.ownerDocument.createElement("p")
      note.textContent = highlight.note.body
      item.append(note)
    }

    const actions = target.ownerDocument.createElement("div")
    actions.className = "annotation-actions"
    actions.append(button(target, highlight.note ? labels.editNote : labels.addNote, "text-button", () => onEdit(highlight)))
    appendShareAction({ target, item, actions, record: highlight, labels, onShare })
    actions.append(button(target, labels.remove, "text-button", () => onRemove(highlight)))
    item.append(actions)
    return item
  }))
}

export function renderBookmarkList({ target, records, labels, labelFor, onNavigate, onShare, onRemove }) {
  if (records.length === 0) return renderEmptyState(target, labels.noBookmarks)

  target.replaceChildren(...records.map((bookmark) => {
    const item = target.ownerDocument.createElement("article")
    item.className = "annotation-card"
    item.append(button(target, labelFor(bookmark), "annotation-quote", () => onNavigate(bookmark)))
    const actions = target.ownerDocument.createElement("div")
    actions.className = "annotation-actions"
    appendShareAction({ target, item, actions, record: bookmark, labels, onShare })
    actions.append(button(target, labels.remove, "text-button", () => onRemove(bookmark)))
    item.append(actions)
    return item
  }))
}

export async function shareAnnotationLocation({ record, request, clipboard }) {
  const response = await request(record.share_url, { method: "POST" })
  if (!response?.url) return null
  if (!clipboard?.writeText) return { url: response.url, copied: false }

  try {
    await clipboard.writeText(response.url)
    return { url: response.url, copied: true }
  } catch (_error) {
    return { url: response.url, copied: false }
  }
}

export function openNoteDialog({ highlight, dialog, title, input, status, colorButtons }) {
  status.textContent = ""
  selectColorButtons(colorButtons, highlight.color)
  title.textContent = highlight.selected_text
  input.value = highlight.note?.body || ""
  dialog.showModal()
  input.focus()
}

export function closeNoteDialog({ dialog, input }) {
  dialog.close()
  input.value = ""
}

export async function saveHighlightNote({ highlight, body, request }) {
  if (!body) {
    if (highlight.note) {
      const response = await request(highlight.note_url, { method: "DELETE" })
      if (response === false) return false
      highlight.note = null
    }
    return true
  }

  const updating = Boolean(highlight.note)
  const note = await request(highlight.note_url, {
    method: updating ? "PATCH" : "POST",
    body: { note: { body } }
  })
  if (!note) return false

  highlight.note = updating ? { ...highlight.note, body } : note
  return true
}

export async function updateHighlightColor({ highlight, color, palette, buttons, status, errorLabel, request, onUpdated }) {
  if (!highlight || !validHighlightColor(palette, color) || highlight.color === color) return false

  buttons.forEach((button) => button.disabled = true)
  selectColorButtons(buttons, color)
  const updated = await request(highlight.url, { method: "PATCH", body: { highlight: { color } } })
  buttons.forEach((button) => button.disabled = false)
  if (!updated) {
    selectColorButtons(buttons, highlight.color)
    status.textContent = errorLabel
    return false
  }

  Object.assign(highlight, updated)
  rememberHighlightColor(color)
  await onUpdated(highlight)
  status.textContent = ""
  return true
}

export function selectHighlightFilter({ color, palette, buttons }) {
  const selected = validHighlightColor(palette, color) ? color : ""
  selectColorButtons(buttons, selected)
  return selected
}

export async function removeHighlightRecord({ highlight, records, request }) {
  if (!highlight) return null

  const response = await request(highlight.url, { method: "DELETE" })
  return response === false ? null : records.filter((record) => record.id !== highlight.id)
}

export async function removeBookmarkRecords({ bookmarks, records, request }) {
  const removedIds = new Set((await Promise.all(bookmarks.map(async (bookmark) => {
    const response = await request(bookmark.url, { method: "DELETE" })
    return response === true ? bookmark.id : null
  }))).filter(Boolean))
  return records.filter((record) => !removedIds.has(record.id))
}

function renderEmptyState(target, label) {
  const empty = target.ownerDocument.createElement("p")
  empty.className = "reader-empty"
  empty.textContent = label
  target.replaceChildren(empty)
}

function appendShareAction({ target, item, actions, record, labels, onShare }) {
  const share = button(target, labels.share, "text-button", async () => {
    share.disabled = true
    const existingFallback = item.querySelector?.(".annotation-share-url")
    existingFallback?.remove()
    status.textContent = ""

    const result = await onShare(record)
    if (result?.copied) {
      target.ownerDocument.defaultView.alert(labels.linkCopied)
    } else if (result?.url) {
      status.textContent = labels.copyManually
      const fallback = target.ownerDocument.createElement("input")
      fallback.type = "text"
      fallback.className = "annotation-share-url"
      fallback.value = result.url
      fallback.readOnly = true
      fallback.setAttribute("aria-label", labels.copyManually)
      item.append(fallback)
      fallback.focus()
      fallback.select()
    } else {
      status.textContent = labels.shareError
    }
    share.disabled = false
  })
  const status = target.ownerDocument.createElement("span")
  status.className = "annotation-share-status"
  status.setAttribute("role", "status")
  status.setAttribute("aria-live", "polite")
  actions.append(share, status)
}

function button(target, label, className, handler) {
  const control = target.ownerDocument.createElement("button")
  control.type = "button"
  control.className = className
  control.textContent = label
  control.addEventListener("click", handler)
  return control
}
